/**
 * ⚠️ DRAFT — REQUIRES LEGAL REVIEW BEFORE LAUNCH ⚠️
 * These terms of service are a template drafted by AI. They must be reviewed
 * and approved by a qualified legal professional before publication.
 */
import React from "react";
import { Link } from "wouter";
import { useLanguage } from "@/contexts/LanguageContext";

const content = {
  en: {
    title: "Terms of Service",
    lastUpdated: "Last updated: August 2026",
    draft: "⚠️ DRAFT — This document requires legal review before launch.",
    backToHome: "← Back to Home",
    sections: [
      {
        heading: "1. Acceptance of Terms",
        body: `By accessing or using Mizano ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service. We reserve the right to modify these terms at any time; continued use after changes constitutes acceptance.`,
      },
      {
        heading: "2. Nature of the Service",
        body: `Mizano is a personal financial record-keeping and visualization tool. The Service automatically parses bank statements, SMS messages, and uploaded images to extract and categorize transaction data for your personal review.

IMPORTANT: Mizano is NOT a financial advisor, tax consultant, accountant, or investment advisor. The Service does not provide financial, tax, accounting, legal, or investment advice of any kind. Any data, charts, summaries, or reports generated by the Service are for informational and personal record-keeping purposes only.`,
      },
      {
        heading: "3. User Responsibilities",
        body: `You are solely responsible for:

• Verifying the accuracy of all parsed and displayed transaction data.
• Ensuring that uploaded documents and messages belong to you or that you have authorization to process them.
• Maintaining the confidentiality of your account credentials.
• Complying with all applicable laws in your jurisdiction regarding the use of financial data.
• Making your own independent financial decisions without reliance on the Service.`,
      },
      {
        heading: "4. Automated Parsing Disclaimer",
        body: `Transaction parsing is performed by automated systems including artificial intelligence and machine learning models. These systems may produce errors including but not limited to:

• Incorrect transaction amounts or dates.
• Misidentified merchants or categories.
• Duplicate or missing transactions.
• Incorrect balance calculations.

You acknowledge that automated parsing is inherently imperfect and agree to verify all data independently. We do not guarantee the accuracy, completeness, or reliability of any parsed data.`,
      },
      {
        heading: "5. Limitation of Liability",
        body: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:

The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.

In no event shall Mizano, its operators, affiliates, or service providers be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or financial losses arising from:

• Your use of or inability to use the Service.
• Errors in parsed transaction data.
• Unauthorized access to your account.
• Any financial decisions made based on information displayed by the Service.

Our total liability for any claim arising from the Service shall not exceed the amount you paid for the Service in the twelve (12) months preceding the claim, or KWD 50, whichever is less.`,
      },
      {
        heading: "6. Account Termination",
        body: `We reserve the right to suspend or terminate your account at our sole discretion, with or without notice, for reasons including but not limited to:

• Violation of these Terms of Service.
• Suspected fraudulent or illegal activity.
• Extended periods of inactivity.
• Technical or security concerns.

You may terminate your account at any time by contacting us. Upon termination, your data will be handled in accordance with our Privacy Policy.`,
      },
      {
        heading: "7. Intellectual Property",
        body: `The Service, including its design, code, features, and branding, is the intellectual property of Mizano's operators. You retain ownership of your personal data and uploaded documents. By using the Service, you grant us a limited license to process your data solely for the purpose of providing the Service.`,
      },
      {
        heading: "8. Indemnification",
        body: `You agree to indemnify and hold harmless Mizano and its operators from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Service, your violation of these terms, or your violation of any third-party rights.`,
      },
      {
        heading: "9. Governing Law",
        body: `These Terms of Service are governed by and construed in accordance with the laws of the State of Kuwait. Any disputes arising from these terms or the Service shall be subject to the exclusive jurisdiction of the courts of Kuwait.`,
      },
      {
        heading: "10. Contact",
        body: `For questions about these Terms of Service:

Email: mizanoapphelp@gmail.com
Location: State of Kuwait`,
      },
    ],
  },
  ar: {
    title: "شروط الخدمة",
    lastUpdated: "آخر تحديث: أغسطس 2026",
    draft: "⚠️ مسودة — يتطلب هذا المستند مراجعة قانونية قبل الإطلاق.",
    backToHome: "→ العودة إلى الرئيسية",
    sections: [
      {
        heading: "1. قبول الشروط",
        body: `باستخدامك لتطبيق ميزانو ("الخدمة")، فإنك توافق على الالتزام بشروط الخدمة هذه. إذا كنت لا توافق على هذه الشروط، فلا تستخدم الخدمة. نحتفظ بالحق في تعديل هذه الشروط في أي وقت؛ ويعتبر استمرار الاستخدام بعد التغييرات بمثابة قبول.`,
      },
      {
        heading: "2. طبيعة الخدمة",
        body: `ميزانو هو أداة لحفظ السجلات المالية الشخصية وعرضها بصرياً. تقوم الخدمة تلقائياً بتحليل كشوف الحسابات البنكية والرسائل النصية والصور المرفوعة لاستخراج بيانات المعاملات وتصنيفها لمراجعتك الشخصية.

مهم: ميزانو ليس مستشاراً مالياً أو ضريبياً أو محاسباً أو مستشار استثمار. لا تقدم الخدمة أي نصائح مالية أو ضريبية أو محاسبية أو قانونية أو استثمارية من أي نوع. أي بيانات أو رسوم بيانية أو ملخصات أو تقارير تنتجها الخدمة هي لأغراض المعلومات وحفظ السجلات الشخصية فقط.`,
      },
      {
        heading: "3. مسؤوليات المستخدم",
        body: `أنت المسؤول الوحيد عن:

• التحقق من دقة جميع بيانات المعاملات المحللة والمعروضة.
• التأكد من أن المستندات والرسائل المرفوعة تخصك أو أن لديك إذناً لمعالجتها.
• الحفاظ على سرية بيانات اعتماد حسابك.
• الامتثال لجميع القوانين المعمول بها في نطاق اختصاصك فيما يتعلق باستخدام البيانات المالية.
• اتخاذ قراراتك المالية المستقلة دون الاعتماد على الخدمة.`,
      },
      {
        heading: "4. إخلاء مسؤولية التحليل الآلي",
        body: `يتم تحليل المعاملات بواسطة أنظمة آلية بما في ذلك الذكاء الاصطناعي ونماذج التعلم الآلي. قد تنتج هذه الأنظمة أخطاء تشمل على سبيل المثال لا الحصر:

• مبالغ أو تواريخ معاملات غير صحيحة.
• تحديد خاطئ للتجار أو الفئات.
• معاملات مكررة أو مفقودة.
• حسابات أرصدة غير صحيحة.

أنت تقر بأن التحليل الآلي غير مثالي بطبيعته وتوافق على التحقق من جميع البيانات بشكل مستقل. لا نضمن دقة أو اكتمال أو موثوقية أي بيانات محللة.`,
      },
      {
        heading: "5. تحديد المسؤولية",
        body: `إلى أقصى حد يسمح به القانون المعمول به:

يتم تقديم الخدمة "كما هي" و"كما هي متاحة" دون أي ضمانات من أي نوع، سواء كانت صريحة أو ضمنية، بما في ذلك على سبيل المثال لا الحصر الضمانات الضمنية للتسويق والملاءمة لغرض معين وعدم الانتهاك.

لن يكون ميزانو أو مشغلوه أو الشركات التابعة له أو مزودو خدماته مسؤولين بأي حال من الأحوال عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية أو عقابية، بما في ذلك على سبيل المثال لا الحصر خسارة الأرباح أو البيانات أو الخسائر المالية الناشئة عن:

• استخدامك أو عدم قدرتك على استخدام الخدمة.
• أخطاء في بيانات المعاملات المحللة.
• الوصول غير المصرح به إلى حسابك.
• أي قرارات مالية تم اتخاذها بناءً على المعلومات المعروضة بواسطة الخدمة.

لن تتجاوز مسؤوليتنا الإجمالية عن أي مطالبة ناشئة عن الخدمة المبلغ الذي دفعته مقابل الخدمة في الاثني عشر (12) شهراً السابقة للمطالبة، أو 50 دينار كويتي، أيهما أقل.`,
      },
      {
        heading: "6. إنهاء الحساب",
        body: `نحتفظ بالحق في تعليق أو إنهاء حسابك وفقاً لتقديرنا الخاص، مع أو بدون إشعار، لأسباب تشمل على سبيل المثال لا الحصر:

• انتهاك شروط الخدمة هذه.
• نشاط احتيالي أو غير قانوني مشتبه به.
• فترات طويلة من عدم النشاط.
• مخاوف تقنية أو أمنية.

يمكنك إنهاء حسابك في أي وقت عن طريق الاتصال بنا. عند الإنهاء، سيتم التعامل مع بياناتك وفقاً لسياسة الخصوصية الخاصة بنا.`,
      },
      {
        heading: "7. الملكية الفكرية",
        body: `الخدمة، بما في ذلك تصميمها وكودها وميزاتها وعلامتها التجارية، هي ملكية فكرية لمشغلي ميزانو. أنت تحتفظ بملكية بياناتك الشخصية ومستنداتك المرفوعة. باستخدامك للخدمة، فإنك تمنحنا ترخيصاً محدوداً لمعالجة بياناتك فقط لغرض تقديم الخدمة.`,
      },
      {
        heading: "8. التعويض",
        body: `أنت توافق على تعويض ميزانو ومشغليه وإبراء ذمتهم من أي مطالبات أو أضرار أو خسائر أو نفقات (بما في ذلك الرسوم القانونية) الناشئة عن استخدامك للخدمة أو انتهاكك لهذه الشروط أو انتهاكك لحقوق أي طرف ثالث.`,
      },
      {
        heading: "9. القانون الحاكم",
        body: `تخضع شروط الخدمة هذه وتُفسر وفقاً لقوانين دولة الكويت. تخضع أي نزاعات ناشئة عن هذه الشروط أو الخدمة للاختصاص القضائي الحصري لمحاكم الكويت.`,
      },
      {
        heading: "10. الاتصال",
        body: `للأسئلة حول شروط الخدمة هذه:

البريد الإلكتروني: mizanoapphelp@gmail.com
الموقع: دولة الكويت`,
      },
    ],
  },
};

export default function Terms() {
  const { language, dir, isRTL } = useLanguage();
  const c = content[language];

  return (
    <div className="min-h-screen bg-background text-foreground safe-area-top safe-area-bottom" dir={dir}>
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link href="/" className="text-primary hover:text-primary/80 text-sm">
            {c.backToHome}
          </Link>
        </div>

        <div className="bg-card text-card-foreground rounded-2xl p-6 md:p-10 shadow-lg border border-border">
          {/* Draft warning */}
          <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-4 mb-6">
            <p className="text-yellow-300 text-sm font-medium">{c.draft}</p>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{c.title}</h1>
          <p className="text-muted-foreground text-sm mb-8">{c.lastUpdated}</p>

          {/* Sections */}
          <div className="space-y-8">
            {c.sections.map((section, idx) => (
              <div key={idx}>
                <h2 className="text-lg font-semibold text-foreground mb-3">{section.heading}</h2>
                <div className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">
                  {section.body}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer links */}
        <div className="mt-8 text-center">
          <Link href="/privacy" className="text-primary hover:text-primary/80 text-sm">
            {language === "en" ? "Privacy Policy" : "سياسة الخصوصية"}
          </Link>
        </div>
      </div>
    </div>
  );
}
